import "@/App.css";
import { Instagram, Linkedin } from "lucide-react";
import { IgTeaser, IgFull } from "@/Instagram.jsx";
import {
    LogoExport,
    LogoSquare,
    LogoSquareDark,
    LogoFavicon,
} from "@/LogoExport.jsx";
import { SlideExport } from "@/SlideExport.jsx";
import { OgExport } from "@/OgExport.jsx";
import { NotifyForm } from "@/NotifyForm.jsx";

const HERO_IMAGE =
    "https://customer-assets.emergentagent.com/job_a4c9c179-d042-4742-b732-bd5e37197666/artifacts/sw53cej4_Image%206.jpeg";

const ComingSoon = () => {
    return (
        <main
            data-testid="hero-section"
            className="relative min-h-screen w-full overflow-hidden bg-[#050C08] text-[#F7F9F5]"
        >
            {/* Hero background image with Ken Burns */}
            <div className="absolute inset-0 z-0">
                <img
                    data-testid="hero-image"
                    src={HERO_IMAGE}
                    alt="BUSHEL vertical farming facility at dusk in Ontario"
                    className="kenburns h-full w-full object-cover"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                />
            </div>

            {/* Cinematic gradient overlay — fades from deep dark on the left to transparent on the right */}
            <div
                aria-hidden="true"
                className="absolute inset-0 z-10 bg-gradient-to-r from-[#050C08]/95 via-[#050C08]/65 to-transparent"
            />
            {/* Bottom vignette to sink content */}
            <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 z-10 h-2/3 bg-gradient-to-t from-[#050C08]/85 via-[#050C08]/30 to-transparent"
            />
            {/* Subtle grain */}
            <div aria-hidden="true" className="grain z-10" />

            {/* Drifting fog layer for depth + atmosphere */}
            <div aria-hidden="true" className="fog-layer z-10" />
            <div aria-hidden="true" className="fog-layer fog-layer-2 z-10" />

            {/* Soft warm glow anchor — sits where the dusk meets the building */}
            <div
                aria-hidden="true"
                className="dusk-glow absolute top-1/3 right-0 z-10 h-[55%] w-[55%] -translate-y-1/4 rounded-full"
            />

            {/* Top bar — wordmark + status */}
            <header className="relative z-20 flex items-center justify-between px-6 pt-7 md:px-12 md:pt-10 lg:px-20">
                <a
                    data-testid="brand-wordmark"
                    href="/"
                    aria-label="Bushel home"
                    className="rise inline-flex items-center"
                >
                    <img
                        data-testid="brand-logo"
                        src="/bushel-wordmark-lime.png"
                        alt="Bushel"
                        width={1426}
                        height={332}
                        decoding="async"
                        className="h-10 w-auto drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] md:h-14"
                    />
                </a>
            </header>

            {/* Vertical accent line */}
            <div
                aria-hidden="true"
                className="absolute top-0 left-6 z-20 hidden h-full w-px md:left-12 lg:left-20 lg:block"
            >
                <div className="line-grow h-full w-full bg-gradient-to-b from-transparent via-white/15 to-transparent" />
            </div>

            {/* Content */}
            <section className="relative z-20 flex min-h-[calc(100vh-7rem)] items-end px-6 pt-16 pb-10 md:px-12 md:pt-32 md:pb-24 lg:px-20 lg:pb-28">
                <div className="max-w-3xl">
                    <p
                        data-testid="launch-note"
                        className="rise font-body mb-4 text-[11px] font-medium tracking-[0.32em] uppercase text-[#89F337] md:mb-6 md:text-xs"
                    >
                        Coming Soon 2026
                    </p>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-[2.6rem] leading-[1.02] font-extrabold tracking-tight text-[#F7F9F5] sm:text-6xl md:text-7xl lg:text-[5.75rem]"
                    >
                        <span className="rise delay-2 block">
                            Grown{" "}
                            <em className="font-medium text-[#89F337] not-italic">
                                clean.
                            </em>
                        </span>
                        <span className="rise delay-3 block">Grown local.</span>
                        <span className="rise delay-4 block">
                            Year{" "}
                            <span className="relative inline-block">
                                round.
                                <span
                                    aria-hidden="true"
                                    className="absolute right-0 -bottom-1 left-0 h-[3px] origin-left scale-x-100 bg-[#89F337]/80"
                                />
                            </span>
                        </span>
                    </h1>

                    <p
                        data-testid="hero-subheadline"
                        className="rise delay-4 font-body mt-6 max-w-2xl text-sm leading-relaxed tracking-wide text-[#C9D3CC] md:mt-8 md:text-lg lg:text-xl"
                    >
                        Pesticide-free, herbicide-free, and fungicide-free,
                        nutrient-rich leafy greens grown year round in an
                        advanced indoor vertical farm, located in Strathroy,
                        Ontario.
                    </p>

                    <a
                        data-testid="partnership-card"
                        href="https://www.yeshealthgroup.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rise delay-4 group mt-7 inline-block max-w-md border-l-2 border-[#89F337]/60 pl-5 transition-colors duration-300 hover:border-[#89F337] md:mt-9"
                    >
                        <p
                            data-testid="partnership-eyebrow"
                            className="font-body text-[10px] font-medium tracking-[0.32em] uppercase text-[#A3B3A8]"
                        >
                            In Partnership With
                        </p>
                        <p
                            data-testid="yeshealth-link"
                            className="mt-2 font-serif text-3xl leading-tight font-medium text-[#F7F9F5] transition-colors duration-300 group-hover:text-[#89F337] md:text-4xl"
                            style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                            YesHealth Group
                        </p>
                        <p
                            data-testid="partnership-subline"
                            className="font-body mt-1.5 text-sm tracking-wide text-[#A3B3A8] md:text-base"
                        >
                            Global vertical farming technology leader
                        </p>
                    </a>

                    <div className="rise delay-5 mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 md:mt-12">
                        <a
                            data-testid="coming-soon-badge"
                            href="mailto:info@bushel.ca"
                            className="cta-glow font-display inline-flex w-full items-center justify-center gap-3 rounded-full border border-white/20 bg-white/10 px-7 py-4 text-sm font-semibold tracking-[0.28em] uppercase text-[#F7F9F5] shadow-[0_8px_32px_rgba(0,0,0,0.35)] backdrop-blur-xl backdrop-saturate-150 transition-colors duration-200 hover:border-[#89F337]/60 hover:text-[#89F337] active:scale-[0.98] sm:w-auto sm:justify-start sm:py-3.5"
                        >
                            <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-[#89F337]" />
                            Contact Us
                        </a>
                        <NotifyForm />
                    </div>
                </div>
            </section>

            {/* Footer credit */}
            <footer className="relative z-20 flex flex-col items-start gap-4 px-6 pb-7 sm:flex-row sm:items-center sm:justify-between md:px-12 md:pb-9 lg:px-20">
                <div className="flex items-center gap-3">
                    <a
                        data-testid="instagram-link"
                        href="https://www.instagram.com/bushelcanada/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Bushel on Instagram"
                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-[#A3B3A8] backdrop-blur-md transition-all duration-200 hover:border-[#89F337]/60 hover:bg-white/10 hover:text-[#89F337] active:scale-95 md:h-9 md:w-9"
                    >
                        <Instagram className="h-[18px] w-[18px] md:h-4 md:w-4" strokeWidth={1.75} />
                    </a>
                    <a
                        data-testid="linkedin-link"
                        href="https://www.linkedin.com/company/bushel-canada"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Bushel on LinkedIn"
                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-[#A3B3A8] backdrop-blur-md transition-all duration-200 hover:border-[#89F337]/60 hover:bg-white/10 hover:text-[#89F337] active:scale-95 md:h-9 md:w-9"
                    >
                        <Linkedin className="h-[18px] w-[18px] md:h-4 md:w-4" strokeWidth={1.75} />
                    </a>
                </div>
                <p
                    data-testid="copyright"
                    className="font-body text-[10px] tracking-[0.18em] text-[#708075] uppercase sm:text-right sm:tracking-[0.28em] sm:text-[11px]"
                >
                    © 2026 Bushel Agritech Inc. All rights reserved. Bushel<sup className="text-[0.7em] align-super">TM</sup>
                </p>
            </footer>

        </main>
    );
};

function App() {
    const path =
        typeof window !== "undefined" ? window.location.pathname : "/";
    let Page = ComingSoon;
    if (path.startsWith("/ig/teaser")) Page = IgTeaser;
    else if (path.startsWith("/ig/full")) Page = IgFull;
    else if (path.startsWith("/logo/favicon")) Page = LogoFavicon;
    else if (path.startsWith("/logo/square-dark")) Page = LogoSquareDark;
    else if (path.startsWith("/logo/square")) Page = LogoSquare;
    else if (path.startsWith("/logo")) Page = LogoExport;
    else if (path.startsWith("/slide")) Page = SlideExport;
    else if (path.startsWith("/og")) Page = OgExport;
    return (
        <div className="App">
            <Page />
        </div>
    );
}

export default App;
